// src/pages/ProProfilePage.js

import React, { useRef, useState, useEffect, useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchProfessionalById } from '../api/queries';
import { TabContext } from '../context/TabContext';
import { AuthContext } from '../context/AuthContext';
import ActionButton from '../components/ActionButton';
import '../components/ModalProfesionalDetail.css';

import { IoPeopleOutline } from 'react-icons/io5';
import { PiPhone }         from 'react-icons/pi';
import { FaWhatsapp }      from 'react-icons/fa';
import { HiOutlineMail }   from 'react-icons/hi';
import { TfiAlarmClock }   from 'react-icons/tfi';

export default function ProProfilePage() {
  const { user } = useContext(AuthContext);
  const professionalId = user?.professional_id;
  const { activeTab } = useContext(TabContext);

  // ─── 1) Fetch profesional ─────────────────────────────────────────────
  const {
    data: profData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['professional', professionalId],
    queryFn: () => fetchProfessionalById(professionalId),
    enabled: !!professionalId,
    staleTime: 5 * 60_000,
    cacheTime: 30 * 60_000
  });

  // ─── 2) Sticky scroll ────────────────────────────────────────────────
  const buttonContainerRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    if (activeTab !== 'prof_profile') {
      setIsStuck(false);
      return;
    }
    const handleScroll = () => {
      if (!buttonContainerRef.current) return;
      setIsStuck(buttonContainerRef.current.getBoundingClientRect().top <= 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [activeTab]);

  const scrollToButtons = () => {
    buttonContainerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ─── 3) Recorte CSS ──────────────────────────────────────────────────
  const containerRef = useRef(null);
  const imgRef       = useRef(null);
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [imgStyle,    setImgStyle]    = useState({});

  useEffect(() => {
    // Default: cover para mostrar la imagen completa
    if (!profData?.cabecera) {
      setImgStyle({
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '0',
        top: '0',
        objectFit: 'cover'
      });
      return;
    }

    const crop = profData.cabecera_crop?.croppedAreaPixels;
    if (!crop || !containerRef.current || naturalSize.width === 0) {
      setImgStyle({
        position: 'absolute',
        width: '100%',
        height: '100%',
        left: '0',
        top: '0',
        objectFit: 'cover'
      });
      return;
    }

    const { x, y, width: cw } = crop;
    const contW = containerRef.current.clientWidth;
    const contH = (contW * 2) / 5; // ratio 5:2
    const scale = contW / cw;
    const scaledW = naturalSize.width  * scale;
    const scaledH = naturalSize.height * scale;

    const leftPos = scaledW < contW
      ? (contW - scaledW) / 2
      : -x * scale;
    const topPos = scaledH < contH
      ? (contH - scaledH) / 2
      : -y * scale;

    setImgStyle({
      position: 'absolute',
      width:  `${scaledW}px`,
      height: `${scaledH}px`,
      left:   `${leftPos}px`,
      top:    `${topPos}px`,
      objectFit: 'cover'
    });
  }, [
    profData?.cabecera,
    profData?.cabecera_crop?.croppedAreaPixels,
    naturalSize.width,
    naturalSize.height
  ]);

  const handleImgLoad = () => {
    if (!imgRef.current) return;
    setNaturalSize({
      width:  imgRef.current.naturalWidth,
      height: imgRef.current.naturalHeight
    });
  };

  // ─── 4) Render ────────────────────────────────────────────────────────
  if (isLoading || !professionalId) {
    return <p style={{ padding: 20 }}>Cargando perfil profesional…</p>;
  }
  if (isError) {
    console.error('[ProProfilePage] Error:', error);
    return <p style={{ padding: 20 }}>Error al cargar el perfil profesional.</p>;
  }

  return (
    <div className="modalProfOverlay">
      <div className="modalProfCard">

        {/* Cabecera recortada */}
        {profData.cabecera && (
          <div className="modalProfHeaderImage" ref={containerRef}>
            <img
              ref={imgRef}
              src={profData.cabecera}
              alt="Cabecera profesional"
              onLoad={handleImgLoad}
              style={imgStyle}
            />
          </div>
        )}

        {/* Información principal */}
        <div className="modalProfInfoTop">
          {profData.tipo && (
            <p className="icons-info">
              <IoPeopleOutline className="icon" />
              <span className="info-text">{profData.tipo}</span>
            </p>
          )}
          <div className="contact-info">
            {profData.telefono && (
              <div className="icons-info">
                <PiPhone className="icon" />
                <span className="info-text">
                  <a href={`tel:${profData.telefono}`}>{profData.telefono}</a>
                </span>
              </div>
            )}
            {profData.whatsapp && (
              <div className="icons-info">
                <FaWhatsapp className="icon" />
                <span className="info-text">
                  <a
                    href={`https://wa.me/34${profData.whatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {profData.whatsapp}
                  </a>
                </span>
              </div>
            )}
          </div>
          {profData.email && (
            <p className="icons-info">
              <HiOutlineMail className="icon" />
              <span className="info-text">
                <a href={`mailto:${profData.email}`}>{profData.email}</a>
              </span>
            </p>
          )}
          {profData.horario && (
            <p className="icons-info">
              <TfiAlarmClock className="icon" />
              <span className="info-text">{profData.horario}</span>
            </p>
          )}
        </div>

        {/* Botones sticky */}
        <div
          ref={buttonContainerRef}
          className={`modalProfButtonContainer ${isStuck ? 'stuck' : ''}`}
        >
          <div className="redesSociales">
            {profData.enlace && (
              <div className="redesSociales-text">
                <a
                  href={profData.enlace}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="web-link"
                >
                  Visita nuestra web
                </a>
              </div>
            )}
            <div className="redesSociales-icons">
              {profData.facebook && (
                <a href={profData.facebook} target="_blank" rel="noopener noreferrer">
                  <img src="/Facebook.png" alt="Facebook" className="social-icon" />
                </a>
              )}
              {profData.instagram && (
                <a href={profData.instagram} target="_blank" rel="noopener noreferrer">
                  <img src="/Instagram.png" alt="Instagram" className="social-icon" />
                </a>
              )}
              {profData.twitter && (
                <a href={profData.twitter} target="_blank" rel="noopener noreferrer">
                  <img src="/Twitter.png" alt="Twitter" className="social-icon" />
                </a>
              )}
              {profData.youtube && (
                <a href={profData.youtube} target="_blank" rel="noopener noreferrer">
                  <img src="/Youtube.png" alt="Youtube" className="social-icon" />
                </a>
              )}
            </div>
          </div>
          <div className="prof-buttons-row">
            <ActionButton label="Acoge"        url={profData.acoge}       onClick={scrollToButtons} />
            <ActionButton label="Apadrina"     url={profData.apadrina}    onClick={scrollToButtons} />
            <ActionButton label="Voluntariado" url={profData.voluntariado} onClick={scrollToButtons} />
            <ActionButton label="Dona"         url={profData.donaciones}   onClick={scrollToButtons} />
            <ActionButton label="Socios"       url={profData.socio}        onClick={scrollToButtons} />
          </div>
        </div>

        {/* Detalles adicionales */}
        <div className="modalProfDetails">
          {profData.aboutus && <p>{profData.aboutus}</p>}
        </div>
      </div>
    </div>
  );
}
